import React, { useState, useEffect } from 'react';
import '../scss/attendees.scss';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { authService } from '../services/authService';
import api from '../services/apiService';
import { FaUserPlus } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import LineupListView from './match/LineupListView';

const ResponseModal = ({ player, onClose, onSubmit, maxPlayers, playingPlayers }) => {
    const [response, setResponse] = useState(player.response || '');
    const [showWarning, setShowWarning] = useState(false);
    
    // Get team configuration from sessionStorage
    const hasMaxPlayers = sessionStorage.getItem('selected_team_has_max_players') !== 'false';
    const hasWaitinglist = sessionStorage.getItem('selected_team_has_waitinglist') !== 'false';

    const handleSubmit = () => {
        // If changing to Yes and already at max players, show waitlist warning only if waitlist is enabled
        if (response === 'Yes' && hasMaxPlayers && hasWaitinglist && 
            playingPlayers.length >= (maxPlayers || 12) && !showWarning) {
            setShowWarning(true);
            return;
        }
        onSubmit(player.player_id, response);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Update Response</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                <h3>{player.first_name} {player.last_name}</h3>
                <div className="response-buttons">
                    <button 
                        className={`response-btn ${response === 'Yes' ? 'active' : ''}`}
                        onClick={() => setResponse('Yes')}
                    >
                        Yes
                    </button>
                    <button 
                        className={`response-btn ${response === 'No' ? 'active' : ''}`}
                        onClick={() => setResponse('No')}
                    >
                        No
                    </button>
                </div>
                {showWarning && (
                    <div className="warning-message">
                        Maximum players reached. This player will be added to the waiting list.
                        Click confirm to proceed.
                    </div>
                )}
                <div className="modal-actions">
                    <button 
                        className="glossy-button" 
                        onClick={handleSubmit}
                    >
                        {showWarning ? 'Confirm' : 'Update Player'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Attendees = ({ attendance, match_id, maxPlayers, type = 'match', matchDetails, selectedTeamType }) => {
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [showLineupModal, setShowLineupModal] = useState(false);
    const [pokeStatus, setPokeStatus] = useState({ canPoke: true, reason: null });
    const [countdown, setCountdown] = useState(null);
    const isAdmin = authService.isAdmin() || authService.isManager();
    
    // Get team configuration from sessionStorage
    const hasMaxPlayers = sessionStorage.getItem('selected_team_has_max_players') !== 'false';
    const hasWaitinglist = sessionStorage.getItem('selected_team_has_waitinglist') !== 'false';
    
    // Fetch poke status
    const fetchPokeStatus = async () => {
        if (!isAdmin) return;
        
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/check_poke_status.php?match_id=${match_id}`);
            if (response.data.success) {
                setPokeStatus({
                    canPoke: response.data.can_poke,
                    reason: response.data.reason
                });
                
                // Set countdown if next poke is available
                if (response.data.next_poke_available) {
                    setCountdown(response.data.next_poke_available);
                } else {
                    setCountdown(null);
                }
            }
        } catch (error) {
            console.error('Error fetching poke status:', error);
        }
    };
    
    // Countdown timer effect
    useEffect(() => {
        if (!countdown) return;
        
        const timer = setInterval(() => {
            const now = new Date().getTime();
            const nextPokeTime = new Date(countdown).getTime();
            const timeLeft = nextPokeTime - now;
            
            if (timeLeft <= 0) {
                setCountdown(null);
                fetchPokeStatus(); // Refresh poke status
            }
        }, 1000);
        
        return () => clearInterval(timer);
    }, [countdown]);
    
    // Format countdown time
    const formatCountdown = (nextPokeTime) => {
        const now = new Date().getTime();
        const timeLeft = new Date(nextPokeTime).getTime() - now;
        
        if (timeLeft <= 0) return null;
        
        const hours = Math.floor(timeLeft / (1000 * 60 * 60));
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };
    
    // Fetch poke status on component mount
    useEffect(() => {
        fetchPokeStatus();
    }, [match_id, isAdmin]);

    const handlePlayerClick = (player) => {
        setSelectedPlayer(player);
    };

    const handleSubmitResponse = async (playerId, response) => {
        try {
            // Check if parent has permission to respond to matches for this child
            if (authService.isParent() && !authService.canRespondToMatches(playerId)) {
                alert('You do not have permission to respond to matches for this child.');
                return;
            }
            
            // Calculate if this should be a waitlist response - only if waitlist is enabled
            const isWaitlist = hasWaitinglist && hasMaxPlayers && 
                response === 'Yes' && playingPlayers.length >= (maxPlayers || 12);

            const res = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    player_id: playerId,
                    response: response,
                    match_id: match_id,
                    is_waitlist: isWaitlist ? "1" : "0"  // Match the string type from API
                })
            });
            
            const data = res.data;
            if (data.success) {
                if (data.notifications_sent > 0) {
                    const notification = document.createElement('div');
                    notification.className = 'response-message info';
                    notification.textContent = `Notifications sent to ${data.notifications_sent} waitlisted player${data.notifications_sent !== 1 ? 's' : ''}.`;
                    document.body.appendChild(notification);
                    
                    setTimeout(() => {
                        notification.classList.add('fade-out');
                        setTimeout(() => {
                            document.body.removeChild(notification);
                        }, 300);
                    }, 5000);
                }
                
                // Refresh the page to show updated data
                window.location.reload();
            } else {
                alert('Failed to update response');
            }
        } catch (error) {
            console.error('Error updating response:', error);
            alert('Error updating response');
        }
    };

    const getUpdatedPlayers = () => {
        const allPlayers = [...attendance.yes, ...attendance.no, ...attendance.awaiting];
        return allPlayers.filter(person => 
            person.response_updated && 
            person.teams_generated && 
            new Date(person.response_updated) > new Date(person.teams_generated)
        );
    };

    const updatedPlayers = getUpdatedPlayers();

    // Sort function by response_updated time (most recent first)
    const sortByResponseTime = (a, b) => {
        if (!a.response_updated) return 1;  // No response time goes to end
        if (!b.response_updated) return -1;  // No response time goes to end
        return new Date(b.response_updated) - new Date(a.response_updated);
    };

    // Separate and sort playing and waitlist players
    const playingPlayers = attendance.yes
        .filter(player => player.is_waitlist === "0")
        .sort(sortByResponseTime)
        .reverse();  // Most recent at bottom

    const waitlistPlayers = attendance.yes
        .filter(player => player.is_waitlist === "1")
        .sort(sortByResponseTime)
        .reverse();  // Most recent at bottom

    // Sort no responses and awaiting
    const noPlayers = [...attendance.no]
        .sort(sortByResponseTime)
        .reverse();  // Most recent at bottom

    const awaitingPlayers = [...attendance.awaiting]
        .sort(sortByResponseTime)
        .reverse();  // Most recent at bottom

    // Calculate the actual playing count (should not exceed max)
    const maxAllowed = maxPlayers || 12;
    const playingCount = Math.min(playingPlayers.length, maxAllowed);
    const waitlistCount = Math.max(0, playingPlayers.length - maxAllowed + waitlistPlayers.length);

    // Calculate how many player slots to display based on team configuration
    // If team has max players, limit to maxAllowed, otherwise use a minimum of 15 slots
    const displaySlots = hasMaxPlayers 
        ? maxAllowed 
        : Math.max(15, playingPlayers.length + 2);

    const handlePokeAwaitingPlayers = async () => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/poke_awaiting_players.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    match_id: match_id,
                    player_ids: awaitingPlayers.map(player => player.player_id)
                })
            });
            
            const data = response.data;
            if (data.success) {
                alert(`Reminder sent to ${data.notifications_sent} player${data.notifications_sent !== 1 ? 's' : ''}`);
                // Refresh poke status after successful poke
                fetchPokeStatus();
            } else {
                alert('Failed to send reminders');
            }
        } catch (error) {
            console.error('Error sending reminders:', error);
            if (error.response?.data?.message) {
                alert(error.response.data.message);
            } else {
                alert('Error sending reminders');
            }
        }
    };

    const handleInvitePlayer = async (playerId) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/invite_to_match.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    match_id: match_id,
                    player_id: playerId
                })
            });
            
            const data = response.data;
            if (data.success) {
                // Show success message before reload
                const notification = document.createElement('div');
                notification.className = 'response-message success';
                notification.textContent = data.message;
                document.body.appendChild(notification);
                
                setTimeout(() => {
                    notification.remove();
                    window.location.reload();
                }, 2000);
            } else {
                // Show error in a notification instead of alert
                const notification = document.createElement('div');
                notification.className = 'response-message error';
                notification.textContent = data.message;
                document.body.appendChild(notification);
                
                setTimeout(() => {
                    notification.remove();
                }, 3000);
            }
        } catch (error) {
            console.error('Error inviting player:', error);
            // Show error in a notification
            const notification = document.createElement('div');
            notification.className = 'response-message error';
            notification.textContent = 'Error inviting player. Please try again.';
            document.body.appendChild(notification);
            
            setTimeout(() => {
                notification.remove();
            }, 3000);
        }
    };

    const formatLastPlayed = (dateString) => {
        if (!dateString) return null;
        return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    };

    // Helper function to capitalize first letter of first name and surname
    const formatPlayerName = (firstName, lastName) => {
        const capitalizeFirst = (str) => {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };
        const firstInitial = firstName ? capitalizeFirst(firstName)[0] : '';
        const capitalizedLastName = capitalizeFirst(lastName);
        return `${firstInitial}. ${capitalizedLastName}`;
    };

    const renderPlayerName = (person, index = null) => {
        const hasUpdatedAfterTeams = person.response_updated && person.response_updated > person.teams_generated;
        const responseTime = person.response_updated 
            ? new Date(person.response_updated).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            })
            : '-';
        
        if (isAdmin) {
            return (
                <li 
                    key={person.player_id} 
                    onClick={() => handlePlayerClick(person)}
                    className="clickable player-name"
                >
                    <div className="player-info">
                        {index !== null && <span className="player-number">{index + 1}.</span>}
                        <span className="player-name-text">
                            {formatPlayerName(person.first_name, person.last_name)} ({person.shirt_number})
                            {hasUpdatedAfterTeams && (
                                <span className="update-indicator" title="Response updated after teams were generated">
                                    <i className="fas fa-exclamation-circle"></i>
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="response-time">{responseTime}</div>
                    <i className="fas fa-pen edit-icon"></i>
                </li>
            );
        }
        
        return (
            <li key={person.player_id}
            className="player-name">
                <div className="player-info">
                    {index !== null && <span className="player-number">{index + 1}.</span>}
                    <span className="player-name-text">
                        {formatPlayerName(person.first_name, person.last_name)}
                        {hasUpdatedAfterTeams && (
                            <span className="update-indicator" title="Response updated after teams were generated">
                                <i className="fas fa-exclamation-circle"></i>
                            </span>
                        )}
                    </span>
                </div>
                <div className="response-time">{responseTime}</div>
            </li>
        );
    };

    const renderCounts = () => {
        // Calculate counts from actual data
        const playingCount = attendance.yes?.filter(player => player.is_waitlist === "0").length || 0;
        const waitlistCount = attendance.yes?.filter(player => player.is_waitlist === "1").length || 0;
        const noCount = attendance.no?.length || 0;
        const awaitingCount = attendance.awaiting?.length || 0;

        return (
            <div className="response-totals">
                <div className="total yes">
                    <span className="count">
                        <span className="current">{playingCount}</span>
                        {hasMaxPlayers && (
                            <span className="max">/ {maxAllowed}</span>
                        )}
                    </span>
                    <span className="label">Yes</span>
                </div>
                <div className="total no">
                    <div className="count">
                        {noCount}
                    </div>
                    <div className="label">No</div>
                </div>
                {hasWaitinglist && (
                    <div className="total waitlist">
                        <div className="count">
                            {waitlistCount}
                        </div>
                        <div className="label">Waiting List</div>
                    </div>
                )}
                <div className="total awaiting">
                    <div className="count">
                        {awaitingCount}
                    </div>
                    <div className="label">Awaiting</div>
                </div>
            </div>
        );
    };

    return (
        <div className="attendance match-details">
            <h3 className="tab-title">Attendance</h3>
            
            {renderCounts()}

            {/* Create Line Up Sheet button for academy and squad teams */}
            {(selectedTeamType === 'academy' || selectedTeamType === 'squad') && isAdmin && (
                <div className="lineup-sheet-section">
                    <button 
                        className="create-lineup-button"
                        onClick={() => setShowLineupModal(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                        Download Line Up Sheet
                    </button>
                </div>
            )}

            <div className="attendance-list">
                <div className="attendance-section">
                    <h3>Playing</h3>
                    <ul>
                        {Array.from({ length: displaySlots }, (_, index) => {
                            // If team has max players, only show players up to the max limit
                            const player = hasMaxPlayers 
                                ? (index < maxAllowed ? playingPlayers[index] : null) 
                                : playingPlayers[index];
                                
                            return player ? (
                                renderPlayerName(player, index)
                            ) : (
                                <li key={`empty-${index}`} className="player-name empty">
                                    <div className="player-info">
                                        <span className="player-number">{index + 1}.</span>
                                        <span className="player-name-text">-</span>
                                    </div>
                                    <div className="response-time">-</div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {hasWaitinglist && waitlistPlayers.length > 0 && (
                    <div className="attendance-section waitlist">
                        <h3>Waiting List</h3>
                        <ul>
                            {waitlistPlayers.map((person, index) => renderPlayerName(person))}
                        </ul>
                    </div>
                )}
                
                <div className="attendance-section">
                    <h3>Not Playing</h3>
                    <ul>
                        {noPlayers.length > 0 ? (
                            noPlayers.map(person => renderPlayerName(person))
                        ) : (
                            <li>-</li>
                        )}
                    </ul>
                </div>
                
                {awaitingPlayers.length > 0 && (
                    <div className="attendance-section">
                        <div className="section-header">
                            <h3>Awaiting Response</h3>
                            {isAdmin && (
                                <div className="poke-container">
                                    <button 
                                        className="poke-button"
                                        disabled={hasMaxPlayers && playingCount >= maxAllowed || !pokeStatus.canPoke}
                                        title={
                                            hasMaxPlayers && playingCount >= maxAllowed ? "Max players reached" : 
                                            !pokeStatus.canPoke ? pokeStatus.reason : ""
                                        }
                                        onClick={() => {
                                            if (window.confirm(`Send reminder to ${awaitingPlayers.length} player${awaitingPlayers.length !== 1 ? 's' : ''}?`)) {
                                                handlePokeAwaitingPlayers();
                                            }
                                        }}
                                    >
                                        Poke <span className="count">({awaitingPlayers.length})</span>
                                    </button>
                                    <div className="poke-info">
                                        <small>
                                            {!pokeStatus.canPoke && pokeStatus.reason ? (
                                                <span className="poke-disabled-info">
                                                    {pokeStatus.reason}
                                                    {countdown && (
                                                        <span className="countdown">
                                                            {' • Next poke in: ' + formatCountdown(countdown)}
                                                        </span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="poke-enabled-info">
                                                    {type === 'training' ? 
                                                        'Available 1 week before training • Once per 48h' : 
                                                        'Available 48h before match • Once per 24h'
                                                    }
                                                </span>
                                            )}
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>
                        <ul>
                            {awaitingPlayers.map(person => renderPlayerName(person))}
                        </ul>
                    </div>
                )}

                {isAdmin && (
                    <div className="attendance-section uninvited">
                        <h3>Invite to {type === 'training' ? 'Training' : 'Match'}</h3>
                        <ul>
                            {attendance?.uninvited?.map(person => (
                                <li key={person.player_id} className="player-name">
                                    <div className="player-info">
                                        <span className="player-name-text">
                                            {formatPlayerName(person.first_name, person.last_name)} ({person.shirt_number})
                                        </span>
                                    </div>
                                    <div className="last-played">
                                        {person.last_played ? (
                                            formatLastPlayed(person.last_played)
                                        ) : (
                                            <span className="never-played">Never played</span>
                                        )}
                                    </div>
                                    <button 
                                        className="invite-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleInvitePlayer(person.player_id);
                                        }}
                                        title="Invite Player"
                                    >
                                        <FaUserPlus />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {selectedPlayer && isAdmin && (
                <ResponseModal 
                    player={selectedPlayer}
                    onClose={() => setSelectedPlayer(null)}
                    onSubmit={handleSubmitResponse}
                    maxPlayers={maxPlayers}
                    playingPlayers={playingPlayers}
                />
            )}

            {/* Lineup Modal for Academy Teams */}
            {showLineupModal && (
                <div className="modal-overlay">
                    <div className="modal-content lineup-modal">
                        <button 
                            className="modal-close" 
                            onClick={() => setShowLineupModal(false)}
                        >
                            ×
                        </button>
                        <LineupListView
                            teams={{
                                white: playingPlayers.map(player => ({
                                    id: player.player_id,
                                    name: `${player.first_name} ${player.last_name}`,
                                    number: player.shirt_number
                                })),
                                colour: []
                            }}
                            teamNames={{
                                white: sessionStorage.getItem('selected_team_name') || 'Team',
                                colour: ''
                            }}
                            match={matchDetails}
                            singleTeamMode={true}
                            playerPool={[]}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Attendees; 